import React from "react";
import { Link } from "react-router-dom";
import { createColumnHelper, FilterFn, Row } from "@tanstack/react-table";
import moment from "moment";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import Tag from "ui-components/molecules/tag/tag";
import TerminalCard from "ui-components/terminal-card/terminalCard";
import TerminalLocation from "ui-components/terminal-location/terminalLocation";
import InboundTrailer from "ui-components/inbound-trailer/inboundTrailer";
import { TagColor } from "app/data/common/tagColor";
import { PlanningFreightProbill, PlanningFreightCategory, PlanningFreightType } from "app/data/linehaul-outbound-planning/models";
import { getStatusColor } from "services/pickups";
import { Routes } from "app/route/RoutesConfig";
import {
  formatDateString,
  formatIsoToLocal,
  formatTimeAgo,
  formatTimeUntilOrAgo,
} from "../utils/relativeTimeUtils";
import Amount from "ui-components/molecules/amount/amount";
import "../outboundPlanningList.scss";

const columnHelper = createColumnHelper<PlanningFreightProbill>();

export type OutboundPlanningListCategory = PlanningFreightCategory | "pickup" | "all";

const HOVER_CARD_POPUP = { keepTooltipInside: false as const };

export interface OutboundPlanningShipmentColumnOptions {
  category: OutboundPlanningListCategory;
  originTerminalNumber: number;
  onPickupNumberClick?: (pickupNumber: string) => void;
}

const formatFreightTypeLabel = (value: PlanningFreightType | undefined): string => {
  switch (value) {
    case "inHouse":
      return "In-House";
    case "inbound":
      return "Inbound";
    case "pickup":
      return "Pickup";
    default:
      return "—";
  }
};

const resolveRowFreightType = (
  row: PlanningFreightProbill,
  category: OutboundPlanningListCategory
): PlanningFreightType => {
  if (category === "all") return row.freightType ?? "inHouse";
  if (category === "pickup") return "pickup";
  return category;
};

const formatFreightStatusLabel = (value: string | null | undefined): string => {
  if (value == null || String(value).trim() === "") return "—";
  const normalized = String(value).replace(/_/g, " ").toLowerCase();
  return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatWeight = (value: number | undefined) =>
  value != null ? value.toLocaleString("en", { useGrouping: true }) : "—";

const formatNameWithNumber = (name?: string | null, number?: number | null): string => {
  if (!name && number == null) return "—";
  if (!name) return String(number);
  if (number == null) return name;
  return `${name} (${number})`;
};

const formatPartyAddress = (row: PlanningFreightProbill, party: "shipper" | "payor" | "consignee"): string => {
  const street =
    party === "shipper"
      ? row.shipperStreetAddress
      : party === "payor"
        ? row.payorStreetAddress
        : row.consigneeStreetAddress;
  const city =
    party === "shipper" ? row.shipperCity : party === "payor" ? row.payorCity : row.consigneeCity;
  const state =
    party === "shipper" ? row.shipperState : party === "payor" ? row.payorState : row.consigneeState;
  const zip =
    party === "shipper"
      ? row.shipperZipcode
      : party === "payor"
        ? row.payorZipcode
        : row.consigneeZipcode;
  const parts = [street, city, state, zip].filter((part) => part != null && String(part).trim() !== "");
  return parts.length ? parts.join(", ") : "—";
};

const sortFacetEntriesIsoKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => {
  const k1 = String(a[0] ?? "");
  const k2 = String(b[0] ?? "");
  const t1 = moment(k1).valueOf();
  const t2 = moment(k2).valueOf();
  const ok1 = Number.isFinite(t1);
  const ok2 = Number.isFinite(t2);
  if (!ok1 && !ok2) return k1.localeCompare(k2);
  if (!ok1) return 1;
  if (!ok2) return -1;
  return t1 - t2;
};

const sortFacetEntriesNumericKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => {
  const k1 = String(a[0] ?? "");
  const k2 = String(b[0] ?? "");
  const n1 = k1 === "" ? NaN : Number(k1);
  const n2 = k2 === "" ? NaN : Number(k2);
  const ok1 = !Number.isNaN(n1);
  const ok2 = !Number.isNaN(n2);
  if (!ok1 && !ok2) return k1.localeCompare(k2);
  if (!ok1) return 1;
  if (!ok2) return -1;
  return n1 - n2;
};

const sortFacetEntriesDateKey = (
  a: [string | number | undefined | null, unknown],
  b: [string | number | undefined | null, unknown]
) => {
  const t1 = moment(String(a[0] ?? ""), "YYYY-MM-DD").valueOf();
  const t2 = moment(String(b[0] ?? ""), "YYYY-MM-DD").valueOf();
  const ok1 = Number.isFinite(t1);
  const ok2 = Number.isFinite(t2);
  if (!ok1 && !ok2) return String(a[0] ?? "").localeCompare(String(b[0] ?? ""));
  if (!ok1) return 1;
  if (!ok2) return -1;
  return t1 - t2;
};

const formatTrailerFilterLabel = (key: string | number | undefined | null): string => {
  if (key == null || key === "") return "—";
  const raw = String(key).trim().toUpperCase();
  return raw.startsWith("TL-") ? raw : `TL-${raw}`;
};

const formatLocationFilterLabel = (key: string | number | undefined | null): string => {
  if (key == null || key === "") return "—";
  const raw = String(key).trim().toUpperCase();
  return raw.startsWith("D-") ? raw : `D-${raw}`;
};

function formatTerminalFilterLabel(row: {
  terminalNumber?: number;
  terminalName?: string;
  airportCode?: string;
}): string {
  const parts = [
    row.terminalNumber != null ? String(row.terminalNumber) : "",
    row.terminalName || "",
    row.airportCode ? `(${row.airportCode})` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

function formatDriverName(row: PlanningFreightProbill): string {
  if (row.driverName?.trim()) return row.driverName.trim();
  const parts = [row.driverFirstName, row.driverLastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "";
}

function renderPickupDriverCell(row: PlanningFreightProbill): React.ReactNode {
  const name = formatDriverName(row);
  return name || "—";
}

function getInHouseLocationFilterValue(row: PlanningFreightProbill): string {
  if (row.freightStatus === "AT_YARD") {
    return row.inboundTrailerNumber != null ? String(row.inboundTrailerNumber) : "";
  }
  return row.location ?? "";
}

function formatInHouseLocationFilterLabel(key: string | number | undefined | null): string {
  const raw = String(key ?? "").trim();
  if (raw === "" || raw === "—") return "—";
  if (/^\d+$/.test(raw)) return formatTrailerFilterLabel(key);
  return formatLocationFilterLabel(key);
}

function renderInHouseLocationCell(row: PlanningFreightProbill, originTerminalNumber: number) {
  if (row.freightStatus === "AT_YARD") {
    return (
      <div className="outbound-planning-location-cell">
        <div className="outbound-planning-location-cell__row1">
          {row.inboundTrailerNumber ? (
            <InboundTrailer
              trailerNumber={row.inboundTrailerNumber}
              inboundManifestNumber={row.inboundManifestNumber}
              terminalNumber={originTerminalNumber}
            />
          ) : (
            "—"
          )}
        </div>
        <div className="outbound-planning-location-cell__row2">
          <Tag mods={{ color: TagColor.YELLOW, flex: true }} mix="outbound-planning-location-meta-tag">
            <XGSIcon icon={XGSIcons.faWarehouse} />
            <span>At Yard</span>
          </Tag>
        </div>
      </div>
    );
  }

  const arrivedAt = row.arrivedAt;
  const ago = arrivedAt ? formatTimeAgo(arrivedAt) : "";
  return (
    <div className="outbound-planning-location-cell">
      <div className="outbound-planning-location-cell__row1">
        <TerminalLocation locationCode={row.location} />
      </div>
      <div className="outbound-planning-location-cell__row2">
        <Tag mods={{ color: TagColor.GREEN, flex: true }} mix="outbound-planning-location-meta-tag">
          <XGSIcon icon={XGSIcons.faWarehouse} />
          <span>{ago ? `At Dock, ${ago}` : "At Dock"}</span>
        </Tag>
      </div>
    </div>
  );
}

function renderInboundLocationCell(row: PlanningFreightProbill, originTerminalNumber: number) {
  const eta = row.inboundEstimatedArrival;
  let row2: React.ReactNode = null;
  if (eta) {
    const { text, isPast } = formatTimeUntilOrAgo(eta);
    if (text) {
      row2 = isPast ? (
        <Tag mods={{ color: TagColor.RED, flex: true }} mix="outbound-planning-location-meta-tag">
          <XGSIcon icon={XGSIcons.faExclamationTriangle} />
          <span>Arriving Late by {text}</span>
        </Tag>
      ) : (
        <Tag mods={{ color: TagColor.BLUE, flex: true }} mix="outbound-planning-location-meta-tag">
          <XGSIcon icon={XGSIcons.faTruck} />
          <span>Arriving in {text}</span>
        </Tag>
      );
    }
  }
  return (
    <div className="outbound-planning-location-cell">
      <div className="outbound-planning-location-cell__row1">
        {row.inboundTrailerNumber ? (
          <InboundTrailer
            trailerNumber={row.inboundTrailerNumber}
            inboundManifestNumber={row.inboundManifestNumber}
            terminalNumber={originTerminalNumber}
          />
        ) : (
          "—"
        )}
      </div>
      {row2 != null && <div className="outbound-planning-location-cell__row2">{row2}</div>}
    </div>
  );
}

function renderEtaWithTags(etaDate: string | null | undefined) {
  const formatted = formatDateString(etaDate);
  if (!formatted) return "—";
  let lateOrAtRiskTag: React.ReactNode = null;
  if (etaDate) {
    const etaMoment = moment(etaDate, "YYYY-MM-DD");
    const today = moment().startOf("day");
    if (etaMoment.isValid()) {
      if (etaMoment.isBefore(today)) {
        lateOrAtRiskTag = <Tag mods={{ color: TagColor.RED }}>Late</Tag>;
      } else if (etaMoment.isSame(today, "day")) {
        lateOrAtRiskTag = <Tag mods={{ color: TagColor.YELLOW }}>At Risk</Tag>;
      }
    }
  }
  return (
    <div className="outbound-planning-eta-cell">
      <span>{formatted}</span>
      {lateOrAtRiskTag}
    </div>
  );
}

const selectFilterMeta = { filterVariant: "select" as const };

const includesStringFilter: FilterFn<PlanningFreightProbill> = (row, columnId, filterValue) => {
  if (filterValue == null || String(filterValue).trim() === "") return true;
  const search = String(filterValue).toLowerCase().trim();
  const raw = row.getValue(columnId);
  const rawStr = String(raw ?? "").toLowerCase();
  const formattedStr =
    typeof raw === "number"
      ? raw.toLocaleString("en", { useGrouping: true }).toLowerCase()
      : rawStr;
  return rawStr.includes(search) || formattedStr.includes(search);
};
includesStringFilter.autoRemove = (val) => val == null || String(val).trim() === "";

export const OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMN_OPTIONS = [
  { id: "expand", label: "Expand" },
  { id: "pickupNumber", label: "Pickup Number" },
  { id: "probillNumber", label: "Probill Number" },
  { id: "shipperName", label: "Shipper" },
  { id: "shipperAddress", label: "Shipper Address" },
  { id: "probillWeight", label: "Weight" },
  { id: "probillPieces", label: "Pieces" },
  { id: "probillSquareYards", label: "Square Yards" },
  { id: "freightStatus", label: "Status" },
  { id: "location", label: "Driver" },
  { id: "bolNumber", label: "BOL" },
  { id: "poNumber", label: "PO" },
  { id: "etaDate", label: "Pickup Date" },
  { id: "consigneeName", label: "Consignee" },
  { id: "consigneeAddress", label: "Consignee Address" },
  { id: "payorName", label: "Payor" },
  { id: "payorAddress", label: "Payor Address" },
  { id: "originTerminal", label: "Origin Terminal" },
  { id: "destinationTerminal", label: "Destination Terminal" },
  { id: "inboundManifestNumber", label: "Inbound Manifest" },
];

export const OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT = [
  "bolNumber",
  "poNumber",
  "probillSquareYards",
  "shipperAddress",
  "consigneeName",
  "consigneeAddress",
  "payorName",
  "payorAddress",
  "originTerminal",
  "destinationTerminal",
  "inboundManifestNumber",
];

export const OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMN_OPTIONS = [
  { id: "expand", label: "Expand" },
  { id: "freightType", label: "Freight Type" },
  { id: "pickupNumber", label: "Pickup Number" },
  { id: "probillNumber", label: "Probill Number" },
  { id: "shipperName", label: "Shipper" },
  { id: "shipperAddress", label: "Shipper Address" },
  { id: "probillWeight", label: "Weight" },
  { id: "probillPieces", label: "Pieces" },
  { id: "probillFreightCharge", label: "Freight Charge" },
  { id: "probillSquareYards", label: "Square Yards" },
  { id: "freightStatus", label: "Status" },
  { id: "location", label: "Location / Driver" },
  { id: "bolNumber", label: "BOL" },
  { id: "poNumber", label: "PO" },
  { id: "billDate", label: "Bill Date" },
  { id: "etaDate", label: "ETA / Pickup Date" },
  { id: "consigneeName", label: "Consignee" },
  { id: "consigneeAddress", label: "Consignee Address" },
  { id: "payorName", label: "Payor" },
  { id: "payorAddress", label: "Payor Address" },
  { id: "originTerminal", label: "Origin Terminal" },
  { id: "destinationTerminal", label: "Destination Terminal" },
  { id: "inboundManifestNumber", label: "Inbound Manifest" },
  { id: "inboundTrailerNumber", label: "Inbound Trailer" },
  { id: "inboundOriginTerminal", label: "Inbound Origin" },
  { id: "inboundEstimatedArrival", label: "Expected Arrival" },
  { id: "inboundDispatchedAt", label: "Inbound Dispatched" },
  { id: "arrivedAt", label: "Arrived At" },
];

export const OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT = [
  "pickupNumber",
  "bolNumber",
  "poNumber",
  "billDate",
  "probillSquareYards",
  "shipperAddress",
  "consigneeName",
  "consigneeAddress",
  "payorName",
  "payorAddress",
  "originTerminal",
  "destinationTerminal",
  "inboundOriginTerminal",
  "inboundTrailerNumber",
  "inboundDispatchedAt",
  "arrivedAt",
];

export const OUTBOUND_PLANNING_SHIPMENT_COLUMN_OPTIONS = [
  { id: "expand", label: "Expand" },
  { id: "probillNumber", label: "Probill Number" },
  { id: "shipperName", label: "Shipper" },
  { id: "shipperAddress", label: "Shipper Address" },
  { id: "probillWeight", label: "Weight" },
  { id: "probillPieces", label: "Pieces" },
  { id: "probillFreightCharge", label: "Freight Charge" },
  { id: "probillSquareYards", label: "Square Yards" },
  { id: "freightStatus", label: "Freight Status" },
  { id: "location", label: "Location" },
  { id: "bolNumber", label: "BOL" },
  { id: "poNumber", label: "PO" },
  { id: "billDate", label: "Bill Date" },
  { id: "etaDate", label: "ETA Date" },
  { id: "consigneeName", label: "Consignee" },
  { id: "consigneeAddress", label: "Consignee Address" },
  { id: "payorName", label: "Payor" },
  { id: "payorAddress", label: "Payor Address" },
  { id: "originTerminal", label: "Origin Terminal" },
  { id: "destinationTerminal", label: "Destination Terminal" },
  { id: "inboundManifestNumber", label: "Inbound Manifest" },
  { id: "inboundTrailerNumber", label: "Inbound Trailer" },
  { id: "inboundOriginTerminal", label: "Inbound Origin" },
  { id: "inboundEstimatedArrival", label: "Expected Arrival" },
  { id: "inboundDispatchedAt", label: "Inbound Dispatched" },
  { id: "arrivedAt", label: "Arrived At" },
];

export const OUTBOUND_PLANNING_SHIPMENT_COLUMNS_HIDDEN_BY_DEFAULT = [
  "bolNumber",
  "poNumber",
  "billDate",
  "probillSquareYards",
  "shipperAddress",
  "consigneeName",
  "consigneeAddress",
  "payorName",
  "payorAddress",
  "originTerminal",
  "destinationTerminal",
  "inboundOriginTerminal",
  "inboundTrailerNumber",
  "inboundDispatchedAt",
  "arrivedAt",
];

export function getOutboundPlanningShipmentColumns(options: OutboundPlanningShipmentColumnOptions) {
  const { category, originTerminalNumber, onPickupNumberClick } = options;
  const isPickup = category === "pickup";
  const isAll = category === "all";
  const showPickupNumber = isPickup || isAll;

  const renderPickupNumberCell = (value: string | undefined) => {
    if (!value) return "—";
    if (onPickupNumberClick) {
      return (
        <span
          className="xgs-table-link-cell"
          onClick={() => onPickupNumberClick(value)}
        >
          {value}
        </span>
      );
    }
    return (
      <Link
        className="xgs-table-link-cell"
        to={`/pickup/management?p=${encodeURIComponent(String(value))}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {value}
      </Link>
    );
  };

  const columns = [
    columnHelper.display({
      id: "expand",
      header: "",
      size: 40,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      enableResizing: false,
      cell: ({ row }) => {
        if (!row.getCanExpand()) return null;
        return (
          <button
            type="button"
            className="outbound-planning-expand-btn"
            onClick={row.getToggleExpandedHandler()}
            aria-label={row.getIsExpanded() ? "Collapse items" : "Expand items"}
          >
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: row.getIsExpanded() ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.15s ease",
              }}
            >
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
        );
      },
    }),
    ...(isAll
      ? [
          columnHelper.accessor((row) => row.freightType ?? "", {
            id: "freightType",
            header: "Freight Type",
            size: 110,
            cell: ({ row }) => formatFreightTypeLabel(row.original.freightType),
            meta: {
              filterVariant: "select" as const,
              filterLabelFormatter: (value) =>
                formatFreightTypeLabel(String(value) as PlanningFreightType),
            },
            filterFn: "equalsString",
          }),
        ]
      : []),
    ...(showPickupNumber
      ? [
          columnHelper.accessor("pickupNumber", {
            id: "pickupNumber",
            header: "Pickup Number",
            size: 110,
            filterFn: includesStringFilter,
            cell: (cellProps) => renderPickupNumberCell(cellProps.getValue()),
          }),
        ]
      : []),
    columnHelper.accessor("probillNumber", {
      id: "probillNumber",
      header: "Probill Number",
      size: 120,
      filterFn: includesStringFilter,
      cell: (cellProps) => {
        const value = cellProps.getValue();
        if (!value) return "—";
        return (
          <Link
            className="xgs-table-link-cell"
            to={`/shipments/${value}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </Link>
        );
      },
    }),
    columnHelper.accessor("shipperName", {
      id: "shipperName",
      header: "Shipper",
      size: 180,
      cell: ({ row }) => formatNameWithNumber(row.original.shipperName, row.original.shipperNumber),
      meta: selectFilterMeta,
      filterFn: "equalsString",
    }),
    columnHelper.accessor((row) => formatPartyAddress(row, "shipper"), {
      id: "shipperAddress",
      header: "Shipper Address",
      size: 220,
      filterFn: includesStringFilter,
      cell: ({ row }) => formatPartyAddress(row.original, "shipper"),
    }),
    columnHelper.accessor("probillWeight", {
      id: "probillWeight",
      header: "Weight",
      size: 90,
      filterFn: includesStringFilter,
      cell: (cellProps) => formatWeight(cellProps.getValue()),
    }),
    columnHelper.accessor("probillPieces", {
      id: "probillPieces",
      header: "Pieces",
      size: 80,
      filterFn: includesStringFilter,
      cell: (cellProps) => formatWeight(cellProps.getValue()),
    }),
    columnHelper.accessor("probillFreightCharge", {
      id: "probillFreightCharge",
      header: "Freight Charge",
      size: 110,
      filterFn: includesStringFilter,
      enableHiding: !isPickup,
      enableColumnFilter: !isPickup,
      enableSorting: !isPickup,
      cell: (cellProps) => {
        const value = cellProps.getValue();
        return value != null ? <Amount amount={value} /> : "—";
      },
    }),
    columnHelper.accessor("probillSquareYards", {
      id: "probillSquareYards",
      header: "Square Yards",
      size: 100,
      filterFn: includesStringFilter,
      cell: (cellProps) => formatWeight(cellProps.getValue()),
    }),
    columnHelper.accessor("freightStatus", {
      id: "freightStatus",
      header: isPickup || isAll ? "Status" : "Freight Status",
      size: 130,
      cell: (cellProps) => {
        const value = cellProps.getValue();
        if (!value) return "—";
        const rowType = resolveRowFreightType(cellProps.row.original, category);
        const rowIsPickup = rowType === "pickup";
        const label = rowIsPickup
          ? String(value).replace(/_/g, " ")
          : formatFreightStatusLabel(value);
        const color = rowIsPickup
          ? getStatusColor(String(value))
          : value === "INBOUND"
            ? TagColor.BLUE
            : value === "AT_YARD"
              ? TagColor.YELLOW
            : value === "STAGED" || value === "RACK"
              ? TagColor.GREEN
              : TagColor.GREY;
        return (
          <Tag mods={{ color, uppercase: rowIsPickup }}>{label}</Tag>
        );
      },
      meta: {
        filterVariant: "select",
        filterLabelFormatter: (value) => {
          const str = String(value);
          if (str.includes("_") && str === str.toUpperCase()) {
            return str.replace(/_/g, " ");
          }
          return formatFreightStatusLabel(str);
        },
      },
      filterFn: "equalsString",
    }),
    columnHelper.accessor(
      (row) => {
        const rowType = resolveRowFreightType(row, category);
        if (rowType === "pickup") {
          return formatDriverName(row) || "";
        }
        if (rowType === "inHouse") return getInHouseLocationFilterValue(row);
        return row.inboundTrailerNumber ?? "";
      },
      {
        id: "location",
        header: isPickup ? "Driver" : isAll ? "Location / Driver" : "Location",
        size: 180,
        cell: ({ row }) => {
          const rowType = resolveRowFreightType(row.original, category);
          if (rowType === "pickup") {
            return renderPickupDriverCell(row.original);
          }
          if (rowType === "inHouse") {
            return renderInHouseLocationCell(row.original, originTerminalNumber);
          }
          return renderInboundLocationCell(row.original, originTerminalNumber);
        },
        meta: isPickup || isAll
          ? {
              filterVariant: "select" as const,
            }
          : {
              filterVariant: "select" as const,
              filterLabelFormatter:
                category === "inHouse"
                  ? formatInHouseLocationFilterLabel
                  : formatTrailerFilterLabel,
            },
        filterFn: "equalsString",
      }
    ),
    columnHelper.accessor("bolNumber", {
      id: "bolNumber",
      header: "BOL",
      size: 100,
      filterFn: includesStringFilter,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("poNumber", {
      id: "poNumber",
      header: "PO",
      size: 100,
      filterFn: includesStringFilter,
      cell: (cellProps) => cellProps.getValue() ?? "—",
    }),
    columnHelper.accessor("billDate", {
      id: "billDate",
      header: "Bill Date",
      size: 100,
      cell: (cellProps) => formatDateString(cellProps.getValue()) || "—",
      meta: {
        filterVariant: "select",
        filterLabelFormatter: (value) => formatDateString(String(value)) || "—",
        filterSortFn: sortFacetEntriesDateKey,
      },
      filterFn: "equals",
    }),
    columnHelper.accessor("etaDate", {
      id: "etaDate",
      header: isPickup ? "Pickup Date" : isAll ? "ETA / Pickup Date" : "ETA Date",
      size: 130,
      cell: (cellProps) => {
        const rowType = resolveRowFreightType(cellProps.row.original, category);
        return rowType === "pickup"
          ? formatDateString(cellProps.getValue()) || "—"
          : renderEtaWithTags(cellProps.getValue());
      },
      meta: {
        filterVariant: "select",
        filterLabelFormatter: (value) => formatDateString(String(value)) || "—",
        filterSortFn: sortFacetEntriesDateKey,
      },
      filterFn: "equals",
    }),
    columnHelper.accessor("consigneeName", {
      id: "consigneeName",
      header: "Consignee",
      size: 160,
      cell: ({ row }) => formatNameWithNumber(row.original.consigneeName, row.original.consigneeNumber),
      meta: selectFilterMeta,
      filterFn: "equalsString",
    }),
    columnHelper.accessor((row) => formatPartyAddress(row, "consignee"), {
      id: "consigneeAddress",
      header: "Consignee Address",
      size: 220,
      filterFn: includesStringFilter,
      cell: ({ row }) => formatPartyAddress(row.original, "consignee"),
    }),
    columnHelper.accessor("payorName", {
      id: "payorName",
      header: "Payor",
      size: 160,
      cell: ({ row }) => formatNameWithNumber(row.original.payorName, row.original.payorNumber),
      meta: selectFilterMeta,
      filterFn: "equalsString",
    }),
    columnHelper.accessor((row) => formatPartyAddress(row, "payor"), {
      id: "payorAddress",
      header: "Payor Address",
      size: 220,
      filterFn: includesStringFilter,
      cell: ({ row }) => formatPartyAddress(row.original, "payor"),
    }),
    columnHelper.accessor(
      (row) =>
        formatTerminalFilterLabel({
          terminalNumber: row.originTerminalNumber,
          terminalName: row.originTerminalName,
          airportCode: row.originTerminalAirportCode,
        }),
      {
        id: "originTerminal",
        header: "Origin Terminal",
        size: 180,
        cell: ({ row }) => {
          const r = row.original;
          if (r.originTerminalNumber == null) return "—";
          return (
            <TerminalCard
              terminalNumber={r.originTerminalNumber}
              terminalName={r.originTerminalName}
              terminalAirportCode={r.originTerminalAirportCode}
              {...HOVER_CARD_POPUP}
            />
          );
        },
        meta: selectFilterMeta,
        filterFn: "equalsString",
      }
    ),
    columnHelper.accessor(
      (row) =>
        formatTerminalFilterLabel({
          terminalNumber: row.destinationTerminalNumber,
          terminalName: row.destinationTerminalName,
          airportCode: row.destinationTerminalAirportCode,
        }),
      {
        id: "destinationTerminal",
        header: "Destination Terminal",
        size: 180,
        cell: ({ row }) => {
          const r = row.original;
          if (r.destinationTerminalNumber == null) return "—";
          return (
            <TerminalCard
              terminalNumber={r.destinationTerminalNumber}
              terminalName={r.destinationTerminalName}
              terminalAirportCode={r.destinationTerminalAirportCode}
              {...HOVER_CARD_POPUP}
            />
          );
        },
        meta: selectFilterMeta,
        filterFn: "equalsString",
      }
    ),
    columnHelper.accessor("inboundManifestNumber", {
      id: "inboundManifestNumber",
      header: "Inbound Manifest",
      size: 130,
      cell: (cellProps) => {
        const manifestNumber = cellProps.getValue();
        if (manifestNumber == null) return "—";
        return (
          <Link
            className="xgs-table-link-cell"
            to={`/${originTerminalNumber}${Routes.freights.inbound}?manifestNumber=${encodeURIComponent(String(manifestNumber))}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {manifestNumber}
          </Link>
        );
      },
      meta: {
        filterVariant: "select",
        filterSortFn: sortFacetEntriesNumericKey,
      },
      filterFn: "weakEquals",
    }),
    columnHelper.accessor("inboundTrailerNumber", {
      id: "inboundTrailerNumber",
      header: "Inbound Trailer",
      size: 120,
      cell: (cellProps) => {
        const trailerNumber = cellProps.getValue();
        if (trailerNumber == null) return "—";
        return (
          <InboundTrailer
            trailerNumber={trailerNumber}
            inboundManifestNumber={cellProps.row.original.inboundManifestNumber}
            terminalNumber={originTerminalNumber}
          />
        );
      },
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatTrailerFilterLabel,
        filterSortFn: sortFacetEntriesNumericKey,
      },
      filterFn: "weakEquals",
    }),
    columnHelper.accessor(
      (row) =>
        formatTerminalFilterLabel({
          terminalNumber: row.inboundOriginTerminalNumber,
          terminalName: row.inboundOriginTerminalName,
          airportCode: row.inboundOriginTerminalAirportCode,
        }),
      {
        id: "inboundOriginTerminal",
        header: "Inbound Origin",
        size: 180,
        cell: ({ row }) => {
          const r = row.original;
          if (r.inboundOriginTerminalNumber == null) return "—";
          return (
            <TerminalCard
              terminalNumber={r.inboundOriginTerminalNumber}
              terminalName={r.inboundOriginTerminalName}
              terminalAirportCode={r.inboundOriginTerminalAirportCode}
              {...HOVER_CARD_POPUP}
            />
          );
        },
        meta: selectFilterMeta,
        filterFn: "equalsString",
      }
    ),
    columnHelper.accessor("inboundEstimatedArrival", {
      id: "inboundEstimatedArrival",
      header: "Expected Arrival",
      size: 150,
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()) || "—",
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatIsoToLocal,
        filterSortFn: sortFacetEntriesIsoKey,
      },
      filterFn: "equals",
    }),
    columnHelper.accessor("inboundDispatchedAt", {
      id: "inboundDispatchedAt",
      header: "Inbound Dispatched",
      size: 150,
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()) || "—",
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatIsoToLocal,
        filterSortFn: sortFacetEntriesIsoKey,
      },
      filterFn: "equals",
    }),
    columnHelper.accessor("arrivedAt", {
      id: "arrivedAt",
      header: "Arrived At",
      size: 150,
      cell: (cellProps) => formatIsoToLocal(cellProps.getValue()) || "—",
      meta: {
        filterVariant: "select",
        filterLabelFormatter: formatIsoToLocal,
        filterSortFn: sortFacetEntriesIsoKey,
      },
      filterFn: "equals",
    }),
  ];

  if (isPickup) {
    const pickupColumnIds = new Set(OUTBOUND_PLANNING_PICKUP_SHIPMENT_COLUMN_OPTIONS.map(({ id }) => id));
    return columns.filter((col) => col.id && pickupColumnIds.has(col.id));
  }

  if (isAll) {
    const allColumnIds = new Set(OUTBOUND_PLANNING_ALL_SHIPMENT_COLUMN_OPTIONS.map(({ id }) => id));
    return columns.filter((col) => col.id && allColumnIds.has(col.id));
  }

  return columns;
}

export function getRowCanExpandFreight(row: Row<PlanningFreightProbill>): boolean {
  return (row.original.items?.length ?? 0) > 0;
}
